import React, { useEffect, useState } from "react";
import { Search, Award, DollarSign, PlayCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function MyMos() {
  const { user } = useAuth();
  const [mos, setMos] = useState([]);
  const [detail, setDetail] = useState(null);
  const [pay, setPay] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/mos").then((r) => setMos(r.data)).catch(() => {});
    api.get("/pay-scale").then((r) => setPay(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.mos) api.get(`/mos/${user.mos}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [user?.mos]);

  const filtered = mos.filter(
    (m) => m.code.toLowerCase().includes(q.toLowerCase()) || m.title.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="mos-page">
      <div className="eyebrow mb-1" style={{ letterSpacing: "0.16em" }}>Military Occupational Specialty</div>
      <h1 className="heading text-4xl mb-6">My MOS</h1>

      {detail && (
        <div className="rsp-card p-5 mb-6 border-[var(--rsp-gold-dim)]/50" data-testid="my-mos-card">
          <div className="flex items-center gap-2 mb-1"><Award className="gold" size={20} />
            <span className="font-mono-rsp text-2xl gold">{detail.code}</span></div>
          <div className="font-head text-2xl uppercase tracking-wide">{detail.title}</div>
          {detail.description && <p className="text-[var(--rsp-gray)] mt-2 leading-relaxed">{detail.description}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
            {detail.training_length && <div className="rounded-lg bg-[var(--rsp-bg-2)] p-2 text-sm"><div className="eyebrow" style={{letterSpacing:"0.12em"}}>Training</div>{detail.training_length}</div>}
            {detail.asvab && <div className="rounded-lg bg-[var(--rsp-bg-2)] p-2 text-sm"><div className="eyebrow" style={{letterSpacing:"0.12em"}}>ASVAB</div>{detail.asvab}</div>}
            {detail.pay && <div className="rounded-lg bg-[var(--rsp-bg-2)] p-2 text-sm"><div className="eyebrow" style={{letterSpacing:"0.12em"}}>Pay</div>{detail.pay}</div>}
          </div>
          {(detail.videos || []).map((v, i) => (
            <a key={v.url || v.title} href={v.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 mt-2 gold text-sm hover:brightness-110" data-testid={`mos-video-${i}`}>
              <PlayCircle size={16} /> {v.title}
            </a>
          ))}
        </div>
      )}

      <section className="mb-6">
        <div className="flex items-center gap-2 eyebrow mb-3" style={{ letterSpacing: "0.16em" }}>
          <DollarSign size={14} /> Base Pay (&lt;2 yrs)
        </div>
        <div className="rsp-card overflow-x-auto">
          <table className="w-full text-xs sm:text-sm" data-testid="pay-scale-table">
            <thead>
              <tr className="border-b border-[var(--rsp-border)] text-[var(--rsp-gray)]">
                <th className="text-left px-2 sm:px-3 py-2 font-head uppercase tracking-wide whitespace-nowrap">Grade</th>
                <th className="text-right px-2 sm:px-3 py-2 font-head uppercase tracking-wide whitespace-nowrap">Active/mo</th>
                <th className="text-right px-2 sm:px-3 py-2 font-head uppercase tracking-wide whitespace-nowrap">Drill</th>
                <th className="text-right px-2 sm:px-3 py-2 font-head uppercase tracking-wide whitespace-nowrap">Weekend</th>
              </tr>
            </thead>
            <tbody>
              {pay.map((r) => (
                <tr key={r.grade} className="border-b border-[var(--rsp-border)]/60 last:border-0 hover:bg-[var(--rsp-bg-2)]">
                  <td className="px-2 sm:px-3 py-2 font-mono-rsp gold whitespace-nowrap">{r.grade}</td>
                  <td className="text-right px-2 sm:px-3 py-2 font-mono-rsp whitespace-nowrap">${r.active_monthly?.toLocaleString()}</td>
                  <td className="text-right px-2 sm:px-3 py-2 font-mono-rsp whitespace-nowrap">${r.guard_drill?.toLocaleString()}</td>
                  <td className="text-right px-2 sm:px-3 py-2 font-mono-rsp whitespace-nowrap">${r.guard_weekend?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[var(--rsp-muted)] text-xs mt-2">Approx. 2025-2026 enlisted base pay. Guard drill = 1 period; weekend = 4 drills.</p>
      </section>

      <section>
        <div className="eyebrow mb-3" style={{ letterSpacing: "0.16em" }}>MOS Catalog · {mos.length}</div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rsp-muted)]" size={16} />
          <input data-testid="mos-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code or title…"
            className="w-full rsp-card pl-9 pr-4 py-3 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)]" />
        </div>
        <div className="space-y-2 max-h-[420px] overflow-y-auto rsp-scroll pr-1">
          {filtered.map((m) => (
            <div key={m.code} className="rsp-card p-3 flex items-start gap-3" data-testid={`mos-${m.code}`}>
              <span className="font-mono-rsp gold w-14 shrink-0">{m.code}</span>
              <div className="font-semibold">{m.title}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
